import React, { useEffect, useRef, useState, memo, useMemo } from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

export const GULF_COUNTRIES = [
  { code: "AE", name: "United Arab Emirates", flag: "🇦🇪", center: { lat: 24.4539, lng: 54.3773 }, zoom: 8 },
  { code: "SA", name: "Saudi Arabia",          flag: "🇸🇦", center: { lat: 23.8859, lng: 45.0792 }, zoom: 6 },
  { code: "KW", name: "Kuwait",                flag: "🇰🇼", center: { lat: 29.3759, lng: 47.9774 }, zoom: 9 },
  { code: "QA", name: "Qatar",                 flag: "🇶🇦", center: { lat: 25.3548, lng: 51.1839 }, zoom: 9 },
  { code: "BH", name: "Bahrain",               flag: "🇧🇭", center: { lat: 26.0667, lng: 50.5577 }, zoom: 10 },
  { code: "OM", name: "Oman",                  flag: "🇴🇲", center: { lat: 21.4735, lng: 55.9754 }, zoom: 7 },
];

export const COUNTRY_FIELDS: any = {
  AE: ["city", "area", "street", "building", "poBox"],
  SA: ["city", "district", "street", "building", "shortAddress", "postalCode"],
  KW: ["governorate", "city", "block", "street", "building", "poBox"],
  QA: ["city", "zone", "street", "building", "poBox"],
  BH: ["governorate", "city", "block", "road", "building"],
  OM: ["governorate", "city", "wilayat", "street", "building", "poBox"],
  OTHER: ["city", "district", "street", "building", "postalCode"],
};

export const FIELD_LABELS: any = {
  city:         { label: "City / Town",             placeholder: "e.g. Dubai" },
  area:         { label: "Area / District / Community", placeholder: "e.g. Downtown Dubai" },
  street:       { label: "Street",                  placeholder: "e.g. Sheikh Zayed Road" },
  building:     { label: "Building Name / Number",  placeholder: "e.g. Emirates Tower" },
  poBox:        { label: "PO Box / Postal Code",    placeholder: "e.g. 12345" },
  region:       { label: "Region / Province",       placeholder: "e.g. Riyadh Region" },
  district:     { label: "District",                placeholder: "e.g. Al Olaya" },
  shortAddress: { label: "Short Add (National Address)", placeholder: "e.g. RHOO2375" },
  postalCode:   { label: "Postal Code",             placeholder: "e.g. 11564" },
  governorate:  { label: "Governorate",             placeholder: "e.g. Capital" },
  block:        { label: "Block",                   placeholder: "e.g. Block 3" },
  zone:         { label: "Zone",                    placeholder: "e.g. Zone 38" },
  road:         { label: "Road / Avenue",           placeholder: "e.g. Road 22" },
  wilayat:      { label: "Wilayat",                 placeholder: "e.g. Muscat" },
};

export const getCountryAddressConfig = (countryCode: string) => {
  switch (countryCode) {
    case "AE":
      return {
        cityLabel: "City / Town",
        cityPlaceholder: "e.g. Dubai",
        districtLabel: "Area / District / Community",
        districtPlaceholder: "e.g. Downtown Dubai",
        zipLabel: "PO Box / Postal Code",
        zipPlaceholder: "e.g. 12345",
        hasShortAddress: false,
        zipRequired: false,
      };
    case "KW":
      return {
        cityLabel: "City / Town",
        cityPlaceholder: "e.g. Kuwait City",
        districtLabel: "Block",
        districtPlaceholder: "e.g. Block 3",
        zipLabel: "PO Box / Postal Code",
        zipPlaceholder: "e.g. 12345",
        hasShortAddress: false,
        zipRequired: false,
      };
    case "QA":
      return {
        cityLabel: "City / Town",
        cityPlaceholder: "e.g. Doha",
        districtLabel: "Zone",
        districtPlaceholder: "e.g. Zone 38",
        zipLabel: "PO Box / Postal Code",
        zipPlaceholder: "e.g. 12345",
        hasShortAddress: false,
        zipRequired: false,
      };
    case "BH":
      return {
        cityLabel: "City / Town",
        cityPlaceholder: "e.g. Manama",
        districtLabel: "Block",
        districtPlaceholder: "e.g. Block 3",
        zipLabel: "Postal Code",
        zipPlaceholder: "e.g. 12345",
        hasShortAddress: false,
        zipRequired: false,
      };
    case "OM":
      return {
        cityLabel: "City / Town",
        cityPlaceholder: "e.g. Muscat",
        districtLabel: "Wilayat",
        districtPlaceholder: "e.g. Muscat",
        zipLabel: "PO Box / Postal Code",
        zipPlaceholder: "e.g. 12345",
        hasShortAddress: false,
        zipRequired: false,
      };
    case "SA":
      return {
        cityLabel: "City / Town",
        cityPlaceholder: "e.g. Riyadh",
        districtLabel: "District",
        districtPlaceholder: "e.g. Al Olaya",
        zipLabel: "Postal Code",
        zipPlaceholder: "e.g. 11564",
        hasShortAddress: true,
        zipRequired: true,
      };
    default:
      return {
        cityLabel: "City / Town",
        cityPlaceholder: "e.g. City",
        districtLabel: "District / Neighbourhood",
        districtPlaceholder: "e.g. District",
        zipLabel: "Postal Code",
        zipPlaceholder: "e.g. 12345",
        hasShortAddress: false,
        zipRequired: false,
      };
  }
};

export const fetchNominatim = async (lat: any, lng: any) => {
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
    if (!res.ok) return null;
    return await res.json();
  } catch(e) {
    console.error("Nominatim fetch error:", e);
    return null;
  }
};

export function parseNominatimResult(data: any, countryCode: any) {
  if (!data || !data.address) return {};
  
  const a = data.address;
  const city = a.city || a.town || a.village || a.municipality || "";
  const street = a.road || "";
  const streetNumber = a.house_number || "";
  const postalCode = a.postcode || "";
  const sublocality = a.suburb || a.neighbourhood || a.quarter || "";
  const adminLevel1 = a.state || a.region || "";
  const adminLevel2 = a.county || a.state_district || "";
  const premise = a.building || a.commercial || a.house_number || "";
  
  const full = data.display_name || "";

  const base = {
    city,
    street: streetNumber && street ? `${streetNumber} ${street}` : street || full.split(",")[0],
    building: premise,
    postalCode,
    fullAddress: full,
  };

  switch (countryCode) {
    case "AE":
      return { ...base, area: sublocality || adminLevel2 || city };
    case "SA":
      return { ...base, region: adminLevel1 || city, district: sublocality || adminLevel2 || city, shortAddress: postalCode };
    case "KW":
      return { ...base, governorate: adminLevel1 || city, block: sublocality || adminLevel2 };
    case "QA":
      return { ...base, zone: sublocality || adminLevel2 || city };
    case "BH":
      return { ...base, governorate: adminLevel1 || city, block: sublocality || adminLevel2, road: street || base.street };
    case "OM":
      return { ...base, governorate: adminLevel1 || city, wilayat: adminLevel2 || city };
    default:
      return base;
  }
}

interface GulfMapPickerProps {
  country: string;
  onPick: (latlng: { lat: number; lng: number }, nominatimData: any) => void;
  pinPosition?: { lat: number; lng: number } | null;
  snappedPosition?: { lat: number; lng: number } | null;
}

function GulfMapPicker({ country, onPick, pinPosition, snappedPosition }: GulfMapPickerProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const markerInstance = useRef<google.maps.Marker | null>(null);
  const [apiLoaded, setApiLoaded] = useState(false);

  const defaultCfg = GULF_COUNTRIES.find(c => c.code === "SA")!;
  const cfg = GULF_COUNTRIES.find((c) => c.code === country) || defaultCfg;

  const onPickRef = useRef(onPick);
  useEffect(() => { onPickRef.current = onPick; }, [onPick]);

  // Load Google Maps API
  useEffect(() => {
    setOptions({
      key: process.env.REACT_APP_GOOGLE_MAPS_KEY || "",
      v: "weekly",
    });

    Promise.all([importLibrary("maps"), importLibrary("marker")])
      .then(() => {
        setApiLoaded(true);
      })
      .catch((err: any) => {
        console.error("Error loading Google Maps API:", err);
      });
  }, []);

  const handleUserPick = (lat: number, lng: number) => {
    const latLng = { lat, lng };
    fetchNominatim(lat, lng).then(data => {
      onPickRef.current(latLng, data ?? {});
    });
  };

  // Initialize Map
  useEffect(() => {
    if (!apiLoaded || !mapRef.current) return;

    const initialPos = snappedPosition ?? pinPosition ?? cfg.center;
    const initialZoom = (snappedPosition ?? pinPosition) ? 16 : cfg.zoom;

    const map = new google.maps.Map(mapRef.current, {
      center: initialPos,
      zoom: initialZoom,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      streetViewControl: false,
      fullscreenControl: false,
    });

    mapInstance.current = map;

    // Create marker if position exists
    const markerPos = snappedPosition ?? pinPosition;
    const marker = new google.maps.Marker({
      position: markerPos || undefined,
      map: markerPos ? map : null,
      draggable: true,
    });

    markerInstance.current = marker;

    // Click listener on Map
    map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const clickedLat = e.latLng.lat();
        const clickedLng = e.latLng.lng();
        
        marker.setPosition({ lat: clickedLat, lng: clickedLng });
        marker.setMap(map);
        handleUserPick(clickedLat, clickedLng);
      }
    });

    // Dragend listener on Marker
    marker.addListener("dragend", () => {
      const pos = marker.getPosition();
      if (pos) {
        handleUserPick(pos.lat(), pos.lng());
      }
    });

    return () => {
      // Clean up listeners
      google.maps.event.clearInstanceListeners(map);
      google.maps.event.clearInstanceListeners(marker);
    };
  }, [apiLoaded]);

  // Handle Country changes
  useEffect(() => {
    if (!mapInstance.current) return;
    const hasPin = !!(snappedPosition ?? pinPosition);
    if (!hasPin) {
      mapInstance.current.setCenter(cfg.center);
      mapInstance.current.setZoom(cfg.zoom);
    }
  }, [country, cfg]);

  // Handle updates to pinPosition or snappedPosition
  useEffect(() => {
    if (!mapInstance.current || !markerInstance.current) return;

    const pos = snappedPosition ?? pinPosition;
    if (pos) {
      markerInstance.current.setPosition(pos);
      markerInstance.current.setMap(mapInstance.current);
      mapInstance.current.panTo(pos);
      
      if (snappedPosition) {
        mapInstance.current.setZoom(16);
      }
    } else {
      markerInstance.current.setMap(null);
    }
  }, [pinPosition?.lat, pinPosition?.lng, snappedPosition?.lat, snappedPosition?.lng]);

  return (
    <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "1px solid #e5e7eb", height: 260 }}>
      <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
      {!apiLoaded && (
        <div style={{ position: "absolute", inset: 0, background: "#f3f4f6", display: "flex", alignItems: "center", zIndex: 2 }}>
          <span style={{ color: "#9ca3af", fontSize: 13 }}>Loading map…</span>
        </div>
      )}
      <div style={{
        position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", zIndex: 10,
        background: "rgba(255,255,255,0.9)", color: "#475569", fontSize: 11, padding: "4px 12px",
        borderRadius: 20, pointerEvents: "none", backdropFilter: "blur(6px)", whiteSpace: "nowrap",
        border: "1px solid #e5e7eb"
      }}>
        Click map or drag pin to auto-fill address
      </div>
    </div>
  );
}

export default memo(GulfMapPicker);
